import { createSlice } from '@reduxjs/toolkit';
import { message as toaster } from 'antd';
import { API } from 'api';
import { uploadFileToS3AndGetKey } from 'store/commonSlice';

const initialState = {};

export const clientInvoiceSliceV2 = createSlice({
	name: 'clientInvoiceStateV2',
	initialState,
	reducers: {},
});
const cleanFilters = (filter_by = {}) => {
	const cleaned = {};
	Object.keys(filter_by || {}).forEach((key) => {
		const value = filter_by[key];
		if (value !== undefined && value !== null && value !== '') {
			cleaned[key] = value;
		}
	});
	return cleaned;
};

/**
 * sac_code — SAC dropdown options
 * @returns {Promise<Array<{value, label, rate}>>}
 */
export const searchSacCodesV2 =
	({ shipping_method } = {}) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'sac_code',
				filter_by: cleanFilters({ shipping_method }),
			},
			version: 'v2',
		};
		const response = await API.common(CREDENTIALS);
		return (response?.data?.sac_codes || []).map((s) => {
			const label = s.label || `${s.sac_code}-${s.gst_rate}%`;
			return {
				// value = label: the same sac_code repeats across rates (18% / Exempt),
				// so sac_code is not unique. label is, and it's what the row/invoice store.
				value: label,
				label,
				sac_code: s.sac_code,
				id: s.id,
				rate: Number(s.gst_rate) || 0,
			};
		});
	};

export const searchClientInvoiceTripsV2 =
	({ search_type = 'client_invoice_trip', limit = 10, offset = 0, ...rest } = {}) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type,
				filter_by: cleanFilters({ ...rest }),
				limit,
				offset,
			},
			version: 'v2',
		};
		return API.common(CREDENTIALS).then((response) => ({
			rows: Array.isArray(response?.data) ? response.data : response?.data?.trips || [],
			count: response?.meta?.count || 0,
		}));
	};

/**
 * ripplr_address
 * @returns {Promise<Array>}
 */
export const searchRipplrAddressV2 =
	(filter_by = {}) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'ripplr_address',
				filter_by: cleanFilters(filter_by),
			},
			version: 'v2',
			showLoading: false,
		};

		try {
			const response = await API.common(CREDENTIALS);
			const data = response?.data || {};
			const rows =
				data.ripplr_addresses ||
				data.ripplr_address ||
				data.addresses ||
				data.rows ||
				(Array.isArray(data) ? data : []);
			return Array.isArray(rows) ? rows : [];
		} catch (error) {
			return [];
		}
	};

/**
 * client_po — list POs for a client/project.
 * `project_ids` is the multi-select project selection from Step 1; `project_id`
 * still works for callers that pass a single one.
 * @returns {Promise<Array<{po_number, valid_till, balance}>>}
 */
export const searchClientPOsV2 =
	({ client_id, project_id, project_ids }) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'client_po',
				filter_by: cleanFilters({ client_id, project_id, project_ids }),
			},
			version: 'v2',
			showLoading: false,
		};

		try {
			const response = await API.common(CREDENTIALS);
			return response?.data?.pos || [];
		} catch (error) {
			return [];
		}
	};

/**
 * client_po
 * @returns {Promise<{id, po_number, valid_till}|null>} the created/existing PO
 */
export const createClientPOV2 =
	({ po_number, client_id, project_id, remark, valid_till, setErrors }) =>
	async () => {
		const CREDENTIALS = {
			url: '/create',
			data: {
				create_type: 'client_po',
				data: cleanFilters({ po_number, client_id, project_id, remark, valid_till }),
			},
			setErrors,
			version: 'v2',
		};

		const response = await API.common(CREDENTIALS);
		return response?.data?.po || null;
	};

/**
 * client_wo — list WOs for a client/project
 * `project_ids` is the multi-select project selection from Step 1; `project_id`
 * still works for callers that pass a single one.
 * @returns {Promise<Array<{wo_number, valid_till}>>}
 */
export const searchClientWOsV2 =
	({ client_id, project_id, project_ids }) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'client_wo',
				filter_by: cleanFilters({ client_id, project_id, project_ids }),
			},
			version: 'v2',
			showLoading: false,
		};

		try {
			const response = await API.common(CREDENTIALS);
			return response?.data?.wos || [];
		} catch (error) {
			return [];
		}
	};

/**
 * client_wo — create (or reactivate) a WO master row.
 * @returns {Promise<{id, wo_number, valid_till}|null>}
 */
export const createClientWOV2 =
	({ wo_number, client_id, project_id, remark, valid_till, setErrors }) =>
	async () => {
		const CREDENTIALS = {
			url: '/create',
			data: {
				create_type: 'client_wo',
				data: cleanFilters({ wo_number, client_id, project_id, remark, valid_till }),
			},
			setErrors,
			version: 'v2',
		};

		const response = await API.common(CREDENTIALS);
		return response?.data?.wo || null;
	};

/**
 * client_billable_trips — Step 1 "Fetch Trips".
 * @returns {Promise<{rows: Array, count: number}>}
 */
export const fetchBillableTripsV2 =
	({ filter_by = {}, showLoading = true }) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'client_billable_trips',
				filter_by: cleanFilters(filter_by),
			},
			version: 'v2',
			showLoading,
		};

		try {
			const response = await API.common(CREDENTIALS);
			return {
				rows: response?.data?.trips || [],
				count: response?.data?.count || 0,
			};
		} catch (error) {
			return { rows: [], count: 0 };
		}
	};

/**
 * client_billable_trips with an uploaded Trip ID sheet — Step 1 "Bulk Fetch".
 *
 * Not built on `fetchBillableTripsV2`: that swallows errors and returns an empty
 * list, which would turn "your file has no Trip ID column" into a silent "no
 * trips found". Errors here must reach the upload modal.
 *
 * @returns {Promise<{rows: Array, count: number, bulkFetch: object|null}>}
 */
export const bulkFetchBillableTripsV2 =
	({ filter_by = {}, entity, file }) =>
	async (dispatch) => {
		const key = await dispatch(uploadFileToS3AndGetKey({ entity, file }));

		const response = await API.common({
			url: '/search',
			data: {
				search_type: 'client_billable_trips',
				filter_by: cleanFilters({ ...filter_by, bulk_trip_ids: { entity, key } }),
			},
			version: 'v2',
			showLoading: true,
		});

		return {
			rows: response?.data?.trips || [],
			count: response?.data?.count || 0,
			bulkFetch: response?.data?.bulk_fetch || null,
		};
	};

/**
 * client_invoice_preview — build an invoice preview (GST/SAC/lines), no persist.
 * @returns {Promise<object|null>} preview payload (tax_mode, line_items, totals…)
 */
export const buildClientInvoicePreviewV2 =
	({ req_data, setErrors }) =>
	async () => {
		const CREDENTIALS = {
			url: '/create',
			data: { create_type: 'client_invoice_preview', data: req_data },
			setErrors,
			version: 'v2',
		};

		const res = await API.common(CREDENTIALS);
		return res?.data || null;
	};

/**
 * client_invoice (create) — finalize & generate the invoice.
 */
export const createClientInvoiceV2 =
	({ req_data, setErrors }) =>
	async () => {
		const CREDENTIALS = {
			url: '/create',
			data: { create_type: 'client_invoice', data: req_data },
			setErrors,
			version: 'v2',
		};

		const res = await API.common(CREDENTIALS);
		if (res?.status === 'success') {
			toaster.success(res.message || 'Invoice generated successfully.');
		}
		return res;
	};

/**
 * client_invoice (search) — list / filter / paginate.
 * @returns {Promise<{rows: Array, count: number}>}
 */
export const getClientInvoiceListV2 =
	({ filter_by = {}, pagination = { page: 1, per_page: 20 } }) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'client_invoice',
				filter_by: cleanFilters(filter_by),
				limit: pagination.per_page,
				offset: (pagination.page - 1) * pagination.per_page,
			},
			version: 'v2',
			showLoading: false,
		};

		try {
			const response = await API.common(CREDENTIALS);
			return {
				rows: response?.data?.invoices || [],
				count:
					response?.meta?.total_count ??
					response?.data?.total_count ??
					response?.data?.count ??
					0,
			};
		} catch (error) {
			return { rows: [], count: 0 };
		}
	};

/**
 * client_invoice (search by id) — full invoice detail (invoice, trips, payments).
 * @returns {Promise<{invoice: object, trips: object, payments: Array}|null>}
 */
export const getClientInvoiceDetailV2 =
	({ id }) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'client_invoice',
				filter_by: cleanFilters({ id }),
			},
			version: 'v2',
			showLoading: true,
		};

		try {
			const response = await API.common(CREDENTIALS);
			return response?.data || null;
		} catch (error) {
			return null;
		}
	};

/**
 * client_invoice_payment — Mark Payment.
 * @returns {Promise<object>} envelope; data = { payment_status, paid, pending, pct }
 */
export const markClientInvoicePaymentV2 =
	({ req_data, setErrors }) =>
	async () => {
		const CREDENTIALS = {
			url: '/update',
			data: { update_type: 'client_invoice_payment', data: req_data },
			setErrors,
			version: 'v2',
		};

		const res = await API.common(CREDENTIALS);
		if (res?.status === 'success') {
			toaster.success(res.message || 'Payment recorded successfully.');
		}
		return res;
	};

/**
 * client_invoice_regenerate — raise a regeneration request
 * @returns {Promise<object>} envelope; data = { request_id, new_invoice_id, new_invoice_no }
 */
export const regenerateClientInvoiceV2 =
	({ req_data, setErrors }) =>
	async () => {
		const CREDENTIALS = {
			url: '/update',
			data: { update_type: 'client_invoice_regenerate', data: req_data },
			setErrors,
			version: 'v2',
		};

		const res = await API.common(CREDENTIALS);
		if (res?.status === 'success') {
			toaster.success(res.message || 'Regeneration request created.');
		}
		return res;
	};

/**
 * client_regeneration_trips — selectable trips when regenerating an invoice
 * (all closed trips for the client/project; on-invoice ones flagged is_on_invoice).
 * Same optional filters as Step-1 trip search (vehicle_type_ids, vehicle_numbers,
 * start_date, end_date); the backend defaults any omitted filter from the invoice
 * itself (billing period, vehicles already on it) when not passed here.
 * @returns {Promise<{rows: Array, count: number}>}
 */
export const fetchRegenerationTripsV2 =
	({ client_invoice_id, vehicle_type_ids, vehicle_numbers, start_date, end_date }) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'client_regeneration_trips',
				filter_by: cleanFilters({
					client_invoice_id,
					vehicle_type_ids,
					vehicle_numbers,
					start_date,
					end_date,
				}),
			},
			version: 'v2',
			showLoading: true,
		};

		try {
			const response = await API.common(CREDENTIALS);
			return {
				rows: response?.data?.trips || [],
				count: response?.data?.count || 0,
			};
		} catch (error) {
			return { rows: [], count: 0 };
		}
	};

/**
 * client_invoice_regenerate_edit
 * @returns {Promise<object>} envelope; data = { id, grand_total }
 */
export const editRegeneratedClientInvoiceV2 =
	({ req_data, setErrors }) =>
	async () => {
		const CREDENTIALS = {
			url: '/update',
			data: { update_type: 'client_invoice_regenerate_edit', data: req_data },
			setErrors,
			version: 'v2',
		};

		const res = await API.common(CREDENTIALS);
		if (res?.status === 'success') {
			toaster.success(res.message || 'Invoice updated.');
		}
		return res;
	};

/**
 * client_invoice_field_change — raise an approval-gated charge edit (TDS / Customer
 * Debit) on an active invoice, with mandatory per-field supporting documents.
 * @returns {Promise<object>} envelope; data = { request_id, changes }
 */
export const submitClientInvoiceFieldChangeV2 =
	({ req_data, setErrors }) =>
	async () => {
		const CREDENTIALS = {
			url: '/update',
			data: { update_type: 'client_invoice_field_change', data: req_data },
			setErrors,
			version: 'v2',
		};

		const res = await API.common(CREDENTIALS);
		if (res?.status === 'success') {
			toaster.success(res.message || 'Charge change submitted for approval.');
		}
		return res;
	};

/**
 * client_invoice_field_changes — edit-history rows for an invoice (detail page).
 * @returns {Promise<{rows: Array, count: number}>}
 */
export const getClientInvoiceFieldChangesV2 =
	({ client_invoice_id }) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'client_invoice_field_changes',
				filter_by: cleanFilters({ client_invoice_id }),
			},
			version: 'v2',
			showLoading: false,
		};

		try {
			const response = await API.common(CREDENTIALS);
			return {
				rows: response?.data?.history || [],
				count: response?.data?.total_count || 0,
			};
		} catch (error) {
			return { rows: [], count: 0 };
		}
	};

/**
 * client_invoice_regenerations — regeneration approvals queue (pending/approved/rejected).
 * @returns {Promise<{rows: Array, count: number}>}
 */
export const getClientInvoiceRegenerationsV2 =
	({ filter_by = {}, pagination = { page: 1, per_page: 20 } }) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'client_invoice_regenerations',
				filter_by: cleanFilters(filter_by),
				limit: pagination.per_page,
				offset: (pagination.page - 1) * pagination.per_page,
			},
			version: 'v2',
			showLoading: false,
		};

		try {
			const response = await API.common(CREDENTIALS);
			return {
				rows: response?.data?.requests || [],
				count: response?.data?.total_count || 0,
			};
		} catch (error) {
			return { rows: [], count: 0 };
		}
	};

/**
 * client_invoice_regenerate_approve — approve/reject a regeneration (Admin, in-app).
 * @returns {Promise<object>} envelope; data = { new_invoice_id, invoice_status }
 */
export const resolveClientInvoiceRegenerationV2 =
	({ req_data, setErrors }) =>
	async () => {
		const CREDENTIALS = {
			url: '/update',
			data: { update_type: 'client_invoice_regenerate_approve', data: req_data },
			setErrors,
			version: 'v2',
		};

		const res = await API.common(CREDENTIALS);
		if (res?.status === 'success') {
			toaster.success(res.message || 'Regeneration resolved.');
		}
		return res;
	};

/**
 * client_invoice_export — CSV export; opens the signed URL when ready.
 * @returns {Promise<string|null>} export_url
 */
export const exportClientInvoicesV2 =
	({ filter_by = {}, setErrors }) =>
	async () => {
		const CREDENTIALS = {
			url: '/create',
			data: {
				create_type: 'client_invoice_export',
				data: { filters: cleanFilters(filter_by) },
			},
			setErrors,
			version: 'v2',
		};

		const res = await API.common(CREDENTIALS);
		const url = res?.data?.export_url;
		if (url) {
			window.open(url);
		}
		return url || null;
	};

export default clientInvoiceSliceV2.reducer;
